import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Logo } from "../components/Logo";
import { EMAIL_DISPLAY, PHONE_DISPLAY } from "../lib/site";
import { useLang } from "../lib/i18n";

const CONTENT = {
  nl: {
    updated: "Laatst bijgewerkt: september 2026",
    back: "Terug naar de site",
    heading: "Algemene voorwaarden",
    intro:
      "Deze algemene voorwaarden zijn van toepassing op alle offertes, overeenkomsten en diensten van 4thscenery. We hebben ze bewust kort en duidelijk gehouden. Heb je vragen? Neem gerust contact met ons op.",
    metaTitle: "Algemene voorwaarden · 4thscenery",
    metaDescription: "De algemene voorwaarden van 4thscenery: duidelijk en kort. Van toepassing op alle offertes, overeenkomsten en diensten.",
    sections: [
      {
        title: "1. Wie zijn wij?",
        blocks: [
          { type: "p", text: `Deze voorwaarden worden gebruikt door 4thscenery (KVK 95131108), een marketingbureau in Nederland. Je bereikt ons via ${EMAIL_DISPLAY} of bel ${PHONE_DISPLAY}.` },
        ],
      },
      {
        title: "2. Toepasselijkheid",
        blocks: [
          {
            type: "ul",
            items: [
              "Deze voorwaarden gelden voor elke offerte, overeenkomst en levering tussen 4thscenery en de opdrachtgever.",
              "Afwijkingen van deze voorwaarden zijn alleen geldig als we die samen schriftelijk of per e-mail zijn overeengekomen.",
              "Algemene (inkoop)voorwaarden van de opdrachtgever wijzen wij uitdrukkelijk van de hand.",
            ],
          },
        ],
      },
      {
        title: "3. Aanbod en totstandkoming",
        blocks: [
          { type: "p", text: "Elk aanbod van ons is vrijblijvend. Een offerte is 30 dagen geldig, tenzij anders vermeld. De overeenkomst komt tot stand zodra je ons voorstel schriftelijk of per e-mail accepteert, of zodra we met jouw instemming starten met de werkzaamheden." },
        ],
      },
      {
        title: "4. Uitvoering van de diensten",
        blocks: [
          { type: "p", text: "Wij voeren alle diensten naar beste kunnen en vakmanschap uit. Dat is een inspanningsverplichting: marketing- en advertentieresultaten hangen van veel factoren af en wij garanderen daarom nooit specifieke posities in zoekmachines, bereikcijfers of aantallen leads, tenzij we dat uitdrukkelijk schriftelijk hebben afgesproken." },
          { type: "p", text: "Wij mogen bij de uitvoering derden inschakelen, zoals advertentieplatforms, hostingpartijen of tools. Op diensten van die derden kunnen hun eigen voorwaarden van toepassing zijn." },
        ],
      },
      {
        title: "5. Verantwoordelijkheden van de klant",
        blocks: [
          {
            type: "ul",
            items: [
              "Je levert tijdig de materialen, gegevens en toegangen die nodig zijn voor de opdracht, zoals logo's, teksten, foto's en accounttoegang.",
              "Je garandeert dat je de rechten hebt op alles wat je aanlevert en dat het geen inbreuk maakt op rechten van derden.",
              "Je geeft feedback en akkoorden binnen een redelijke termijn, zodat de planning niet vertraagt.",
            ],
          },
        ],
      },
      {
        title: "6. Planning en levertijden",
        blocks: [
          { type: "p", text: "Genoemde levertijden zijn indicatief en nooit fatale termijnen. Loopt iets uit door late aanlevering of gewijzigde wensen, dan schuift de planning in overleg mee. We houden je daar altijd proactief van op de hoogte." },
        ],
      },
      {
        title: "7. Vergoeding en betaling",
        blocks: [
          {
            type: "ul",
            items: [
              "Alle prijzen zijn in euro's en exclusief btw, tenzij anders vermeld.",
              "Facturen hebben een betalingstermijn van 14 dagen, tenzij anders overeengekomen.",
              "Bezwaar tegen een factuur maak je binnen 14 dagen na factuurdatum; daarna staat het gefactureerde bedrag vast.",
              "Bij te late betaling zijn de wettelijke (handels)rente en redelijke incassokosten verschuldigd en mogen wij de werkzaamheden opschorten tot de betaling is voldaan.",
            ],
          },
        ],
      },
      {
        title: "8. Intellectueel eigendom",
        blocks: [
          { type: "p", text: "Alle door ons ontwikkelde materialen, zoals concepten, video's, websites, teksten, designs en systemen, blijven eigendom van 4thscenery tot de facturen volledig zijn voldaan. Daarna krijg je een onbeperkt gebruiksrecht voor je eigen bedrijf." },
          { type: "p", text: "Wij mogen geleverd werk tonen in ons portfolio en in cases op onze website en social media, tenzij we schriftelijk iets anders afspreken. Door jou aangeleverd materiaal blijft uiteraard jouw eigendom." },
        ],
      },
      {
        title: "9. Vertrouwelijkheid",
        blocks: [
          { type: "p", text: "Beide partijen gaan vertrouwelijk om met niet-openbare informatie die ze tijdens de samenwerking van elkaar krijgen, zoals bedrijfsgegevens, strategie en tarieven." },
        ],
      },
      {
        title: "10. Aansprakelijkheid",
        blocks: [
          { type: "p", text: "Onze aansprakelijkheid is beperkt tot het bedrag dat voor de betreffende opdracht is gefactureerd. Indirecte schade, gevolgschade, gederfde omzet of gemiste besparingen zijn uitgesloten. Deze beperking geldt niet in geval van opzet of bewuste roekeloosheid." },
        ],
      },
      {
        title: "11. Wijziging en beëindiging",
        blocks: [
          { type: "p", text: "Een lopende samenwerking kan in overleg worden gewijzigd of beëindigd. Reeds verrichte werkzaamheden, gemaakte kosten en gereserveerde capaciteit worden in dat geval gefactureerd." },
        ],
      },
      {
        title: "12. Toepasselijk recht",
        blocks: [
          { type: "p", text: "Op deze voorwaarden en op al onze overeenkomsten is Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter in Nederland." },
        ],
      },
    ],
  },
  en: {
    updated: "Last updated: September 2026",
    back: "Back to the site",
    heading: "Terms of Service",
    intro:
      "These terms of service apply to all quotations, agreements and services of 4thscenery. We have deliberately kept them short and clear. Any questions? Feel free to contact us.",
    metaTitle: "Terms of Service · 4thscenery",
    metaDescription: "The terms of service of 4thscenery: clear and short. Applicable to all quotations, agreements and services.",
    sections: [
      {
        title: "1. Who are we?",
        blocks: [
          { type: "p", text: `These terms are used by 4thscenery (Dutch Chamber of Commerce no. 95131108), a marketing agency based in the Netherlands. You can reach us at ${EMAIL_DISPLAY} or call ${PHONE_DISPLAY}.` },
        ],
      },
      {
        title: "2. Applicability",
        blocks: [
          {
            type: "ul",
            items: [
              "These terms apply to every quotation, agreement and delivery between 4thscenery and the client.",
              "Deviations from these terms are only valid if we have agreed to them together in writing or by email.",
              "We expressly reject any general (purchasing) terms and conditions of the client.",
            ],
          },
        ],
      },
      {
        title: "3. Offer and formation",
        blocks: [
          { type: "p", text: "Every offer we make is without obligation. A quotation is valid for 30 days unless stated otherwise. The agreement is formed as soon as you accept our proposal in writing or by email, or as soon as we start the work with your consent." },
        ],
      },
      {
        title: "4. Performance of the services",
        blocks: [
          { type: "p", text: "We perform all services to the best of our knowledge and craftsmanship. This is a best-efforts obligation: marketing and advertising results depend on many factors and we therefore never guarantee specific search engine positions, reach figures or numbers of leads, unless we have explicitly agreed to this in writing." },
          { type: "p", text: "We may engage third parties in the performance, such as advertising platforms, hosting parties or tools. The terms of those third parties may apply to their services." },
        ],
      },
      {
        title: "5. Responsibilities of the client",
        blocks: [
          {
            type: "ul",
            items: [
              "You provide the materials, data and access required for the assignment in good time, such as logos, texts, photos and account access.",
              "You guarantee that you hold the rights to everything you provide and that it does not infringe the rights of third parties.",
              "You provide feedback and approvals within a reasonable period, so the planning is not delayed.",
            ],
          },
        ],
      },
      {
        title: "6. Planning and delivery times",
        blocks: [
          { type: "p", text: "Stated delivery times are indicative and never strict deadlines. If something is delayed due to late delivery of materials or changed requirements, the planning moves along in consultation. We always keep you proactively informed." },
        ],
      },
      {
        title: "7. Fees and payment",
        blocks: [
          {
            type: "ul",
            items: [
              "All prices are in euros and exclusive of VAT, unless stated otherwise.",
              "Invoices have a payment term of 14 days, unless agreed otherwise.",
              "Objections to an invoice must be made within 14 days of the invoice date; after that the invoiced amount is final.",
              "In the event of late payment, statutory (commercial) interest and reasonable collection costs are due, and we may suspend the work until payment has been made.",
            ],
          },
        ],
      },
      {
        title: "8. Intellectual property",
        blocks: [
          { type: "p", text: "All materials we develop, such as concepts, videos, websites, texts, designs and systems, remain the property of 4thscenery until the invoices have been paid in full. After that you receive an unlimited right of use for your own business." },
          { type: "p", text: "We may show delivered work in our portfolio and in cases on our website and social media, unless we agree otherwise in writing. Materials you provide remain your property, of course." },
        ],
      },
      {
        title: "9. Confidentiality",
        blocks: [
          { type: "p", text: "Both parties treat non-public information they receive from each other during the collaboration as confidential, such as business data, strategy and rates." },
        ],
      },
      {
        title: "10. Liability",
        blocks: [
          { type: "p", text: "Our liability is limited to the amount invoiced for the assignment in question. Indirect damage, consequential damage, lost turnover or missed savings are excluded. This limitation does not apply in the event of intent or deliberate recklessness." },
        ],
      },
      {
        title: "11. Changes and termination",
        blocks: [
          { type: "p", text: "An ongoing collaboration can be changed or terminated in consultation. Work already performed, costs incurred and reserved capacity will be invoiced in that case." },
        ],
      },
      {
        title: "12. Applicable law",
        blocks: [
          { type: "p", text: "These terms and all our agreements are governed by Dutch law. Disputes will be submitted to the competent court in the Netherlands." },
        ],
      },
    ],
  },
};

const Section = ({ title, children, testid }) => (
  <section className="mt-10" data-testid={testid}>
    <h2 className="font-archivo text-xl font-extrabold tracking-tight text-cream">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-tan sm:text-base">{children}</div>
  </section>
);

const Block = ({ block }) =>
  block.type === "ul" ? (
    <ul className="list-disc space-y-2 pl-5">
      {block.items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  ) : (
    <p>{block.text}</p>
  );

export default function Terms() {
  const { lang, setLang } = useLang();
  const c = CONTENT[lang];

  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]');
    const prevTitle = document.title;
    const prevDesc = meta?.getAttribute("content");
    document.title = c.metaTitle;
    if (meta) meta.setAttribute("content", c.metaDescription);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [lang]);

  return (
    <div className="min-h-screen bg-ink" data-testid="terms-page">
      <header className="border-b border-white/10 bg-[#222222]">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Logo />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 rounded-full border border-white/15 bg-white/5 p-1" data-testid="terms-lang-toggle">
              {["nl", "en"].map((code) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  data-testid={`terms-lang-${code}`}
                  className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                    lang === code ? "bg-amber font-bold text-[#181510]" : "text-white/50 hover:text-white"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
            <Link to="/" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-amber" data-testid="terms-back-link">
              <ArrowLeft size={13} />
              {c.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-14">
        <div className="flex items-center gap-3">
          <FileText size={22} className="text-amberdeep" />
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-faint">{c.updated}</p>
        </div>
        <h1 className="mt-4 font-archivo text-4xl font-black tracking-tight text-cream" data-testid="terms-heading">
          {c.heading}
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-tan sm:text-base" data-testid="terms-intro">
          {c.intro}
        </p>

        {c.sections.map((s, i) => (
          <Section key={i} title={s.title} testid={`terms-section-${i}`}>
            {s.blocks.map((b, j) => (
              <Block key={j} block={b} />
            ))}
          </Section>
        ))}
      </main>

      <footer className="border-t border-edge/40 py-8">
        <p className="text-center font-mono text-[11px] uppercase tracking-[0.2em] text-faint" data-testid="terms-footer">
          © 2026 4thscenery · Beyond The Ordinary
        </p>
      </footer>
    </div>
  );
}
